import { useEffect } from 'react';

// Tonal suppression for the roaming set.
//
// Every gate the roamer has today is TECHNICAL — reduced motion, touch device,
// hidden tab, open modal, focused field, event shot in flight. None of them
// stop Otto hopping cheerfully across a screen reporting five overdue briefs
// and a brief that closed with no responses. That screen passes every existing
// check and the roam is still wrong.
//
// DECLARED, NOT INFERRED. A surface says "I am dominated by a negative state";
// nothing here scans the DOM for red text or counts warning icons. Inference
// would be wrong in both directions — a single amber chip on a healthy page
// would silence him, and a genuinely bleak screen built from neutral markup
// would not. The pages know; they should say.
//
// This is only about tone. It never makes Otto appear, only stay away, so the
// failure mode of a page forgetting to declare is the behaviour we have now.

let declarations = 0;
const listeners = new Set();

const notify = () => { listeners.forEach((fn) => { try { fn(declarations > 0); } catch (e) { /* a bad listener must not break the gate */ } }); };

// True while any mounted surface has declared itself negative.
export function isNegativeMood() {
  return declarations > 0;
}

// Ref-counted on purpose. Two components on one page can each declare — an
// overdue list and a failed-brief banner, say — and the mood must only lift
// when the last of them releases. With a plain boolean the first unmount would
// clear a flag the second still needed, and Otto would roam onto a screen that
// is still bad news.
//
// The returned release is idempotent, so a double-invoke (StrictMode mounts
// effects twice in development) cannot drive the count negative.
export function declareNegativeMood() {
  declarations += 1;
  notify();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    declarations = Math.max(0, declarations - 1);
    notify();
  };
}

export function subscribeMood(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Test-only: module state outlives a test file otherwise.
export function __resetMood() {
  declarations = 0;
  listeners.clear();
}

// Declare for as long as `active` is true and this component is mounted.
//
//   useNegativeMood(overdue.length > 0);
//
// Passing a falsy value is the normal way to stop declaring — the surface stays
// mounted, the mood lifts. Unmounting releases too.
export default function useNegativeMood(active = true) {
  useEffect(() => {
    if (!active) return undefined;
    const release = declareNegativeMood();
    return release;
  }, [active]);
}
